import { GetStaticProps, GetStaticPaths } from 'next';
import type { NextPage } from 'next';
import logger from '3-foundation/Logging/logger';
import { withSiteLogContext } from '3-foundation/Logging/withSiteLogContext';
import { Layout } from '1-project/Layout/Layout';
import { getDictionaryItems } from '3-foundation/Dictionary/repository';
import { getNavigation } from '3-foundation/Navigation/repository';
import { getSite, getSiteConfig } from '3-foundation/Site/repository';
import { getVacancies, getVacancyDetail } from '3-foundation/Jobs/repository';
import { JobPage } from '1-project/Pages/JobPage';
import { JobAgentPage } from '1-project/Pages/JobAgentPage';
import { PageProps } from '3-foundation/Types/page/pageProps';
import Application from '3-foundation/Application/ApplicationEntity';
import { getPageContent } from '3-foundation/PageContent/repository';
import { jobDetailsSuffix } from '3-foundation/Jobs/jobDetailsSuffix';
import { unsolicitedApplicationSuffix } from '3-foundation/Jobs/unsolicitedApplicationSuffix';
import { isPublicUnsolicitedApplication } from '3-foundation/Jobs/isPublicUnsolicitedApplication';
import { jobAgentSlug } from '3-foundation/Jobs/jobAgentSlug';

type JobPageProps = PageProps & {
	vacancy?: Mindkey.VacancyDetail;
	vacancyId?: string;
	customerId: string;
	isJobAgent?: boolean;
	isUnsolicitedApplication?: boolean;
};

const Page: NextPage<JobPageProps> = (pageProps: JobPageProps) => {
	const { vacancy, vacancyId, customerId, content, isJobAgent, isUnsolicitedApplication } = pageProps ?? {};

	return (
		<Layout {...pageProps}>
			{isJobAgent ? (
				<JobAgentPage customerId={customerId} />
			) : (
				<JobPage
					vacancy={vacancy}
					vacancyId={vacancyId}
					customerId={customerId}
					content={content}
					isUnsolicitedApplication={isUnsolicitedApplication}
				/>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	return {
		paths: [],
		fallback: 'blocking',
	};
};

export const getStaticProps: GetStaticProps<JobPageProps> = withSiteLogContext(async (context) => {
	const { params, locale, preview } = context;
	const { hostname: encodedHostName, id } = params;
	const hostName = atob((encodedHostName ?? '') as string);

	const profiler = logger.startTimer();
	try {
		const { id: siteId, defaultLocale } = await getSiteConfig({ host: hostName, preview });

		const slugArr = id as string[];
		const prefetch = slugArr?.includes('_sites');
		let websitePath = '';

		if (!prefetch) websitePath = id ? slugArr.join('/') : '';
		if (prefetch) websitePath = id ? slugArr.slice(3).join('/') : '';

		if (locale === 'default') {
			return { redirect: { destination: `/${defaultLocale}/${websitePath}`, permanent: false }, revalidate: 60 };
		}

		const site = await getSite({ siteId, locale, preview, host: hostName });
		const navigation = await getNavigation({ siteId, locale, preview, root: site.root });
		const dictionary = await getDictionaryItems({ locale, preview });
		const idArr = id as string[];
		// The job slug is the last segment, the job list page can be nested at any depth
		const jobIdUrl = idArr.at(-1);
		const umbracoPath = idArr.slice(0, -1).join('/');
		const isJobAgent = jobIdUrl === jobAgentSlug[locale];
		const isUnsolicitedApplication = jobIdUrl === unsolicitedApplicationSuffix[locale];

		if (!site.mindkeyCustomerId) {
			logger.error(`Mindkey customer ID is not configured for site: ${hostName}`);
			return { notFound: true, revalidate: 15 };
		}

		const serializedApplication = JSON.parse(JSON.stringify(Application));

		const content = await getPageContent({
			siteId,
			site,
			locale,
			path: umbracoPath,
			preview,
		});

		const baseProps = {
			content,
			navigation,
			dictionary,
			site,
			application: serializedApplication,
			preview: preview || null,
			customerId: site.mindkeyCustomerId,
		};

		if (isJobAgent) {
			return {
				props: { ...baseProps, isJobAgent: true },
				revalidate: 60,
			};
		}

		const jobId = isUnsolicitedApplication
			? (await getVacancies({ customerId: site.mindkeyCustomerId })).find(isPublicUnsolicitedApplication)?.id.trim()
			: jobIdUrl.replace(`-${jobDetailsSuffix[locale]}`, '');

		if (!jobId) {
			return { notFound: true, revalidate: 15 };
		}

		const vacancy = await getVacancyDetail({ customerId: site.mindkeyCustomerId, id: jobId, locale });

		return {
			props: { ...baseProps, vacancy, vacancyId: jobId, isUnsolicitedApplication },
			revalidate: 60,
		};
	} catch (error) {
		const getStatusCode = (err): number | undefined => {
			if (err?.statusCode) return err.statusCode;
			if (err?.cause) return getStatusCode(err.cause);
			return undefined;
		};

		const statusCode = getStatusCode(error);

		if (statusCode === 404) {
			return { notFound: true, revalidate: 15 };
		}

		logger.error(`[${statusCode ?? 'no status'}] getStaticProps() for job page \nCannot get static props \n${error}`, { statusCode });
		throw error;
	} finally {
		profiler.done({ message: 'getStaticProps (job)', level: 'debug' });
	}
});

export default Page;
